import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
    BaseMediaDocument,
    CapacitorPluginService,
    DeleteGalleryMediaDto,
    GalleryCategory,
    GalleryService,
    Media,
    MediaCollectionName,
    MediaData,
    MediaDocumentType,
    MediaMetadata,
    MediaService,
    MediaUploadItem,
    NetworkStatusService,
    PinMediaDto,
    THUMBNAIL_PIXEL_DIMENSIONS
} from '@caballus/ui-common';
import { ImageOptions } from '@capacitor/camera';
import { AddItemToQueueAction } from '@ion-caballus/core/state/actions';
import { Store } from '@ngxs/store';
import { createObjectID } from 'mongo-object-reader';
import { Observable, combineLatest, forkJoin, from, of, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, map, share, switchMap, take, tap } from 'rxjs/operators';
import { HorseCache } from '../../caches/horse/horse.cache';
import { StorageService } from '../../services/storage.service';
import { ThumbnailService } from '../../services/thumbnail.service';
import { base64Size, bytesToSize, serialSwitchMap } from '../../util';

enum Keys {
    GalleryImageThumbnailsMetadata = 'gallery-image-thumbnails-metadata-',
    GalleryImagesRequiringServerThumbnail = 'gallery-images-requiring-server-thumbnail-',
    UnsyncedGalleryImagesMetadata = 'unsynced-gallery-images-metadata-',
    UnsyncedGalleryVideosMetadata = 'unsynced-gallery-videos-metadata-',
    CurrentRideImagesMetadata = 'current-ride-images-metadata-',
    UnsyncedRideImagesMetadata = 'unsynced-ride-images-metadata-',
    CurrentRideVideosMetadata = 'current-ride-videos-metadata-',
    UnsyncedRideVideosMetadata = 'unsynced-ride-videos-metadata-',
    UnsyncedPinActions = 'unsynced-pin-actions-',
    UnsyncedGalleryMediaToDelete = 'unsynced-gallery-media-to-delete-',
    ImageBlobFull = 'image-blob-full-',
    ImageBlobThumbnail = 'image-blob-thumbnail-'
}

export { Keys as MediaCacheKeys };

@Injectable({ providedIn: 'root' })
export class MediaCache {
    private _currentUnsyncedChangesUpload$: Observable<void> = null;
    private _currentUnsyncedGalleryImagesUpload$: Observable<void> = null;
    private _currentUnsyncedPinActionsUpload$: Observable<void> = null;
    private _currentUnsyncedGalleryDeletesUpload$: Observable<void> = null;
    private _currentGalleryImageThumbnailsDownload$: Observable<void> = null;

    constructor(
        private readonly _storageService: StorageService,
        private readonly _capacitorPluginService: CapacitorPluginService,
        private readonly _galleryService: GalleryService,
        private readonly _horseCache: HorseCache,
        private readonly _sanitizer: DomSanitizer,
        private readonly _thumbnailService: ThumbnailService,
        private readonly _mediaService: MediaService,
        private readonly _store: Store,
        private readonly _networkStatusService: NetworkStatusService
    ) {}

    public sync(): Observable<void> {
        return this._networkStatusService.networkStatus$.pipe(
            switchMap(network =>
                !network.isOnline
                    ? of(undefined)
                    : this._uploadUnsyncedChanges().pipe(
                          switchMap(() => this._syncHorseGalleryImageThumbnailsCache())
                      )
            )
        );
    }

    private _uploadUnsyncedChanges(): Observable<void> {
        if (!this._currentUnsyncedChangesUpload$) {
            this._currentUnsyncedChangesUpload$ = this._uploadUnsyncedGalleryImages().pipe(
                switchMap(() => this._syncPinActionsCache()),
                switchMap(() => from(this._storageService.clearUserData(Keys.UnsyncedPinActions))),
                switchMap(() => this._syncGalleryDeletesCache()),
                switchMap(() =>
                    from(this._storageService.clearUserData(Keys.UnsyncedGalleryMediaToDelete))
                ),
                tap(() => (this._currentUnsyncedChangesUpload$ = null)),
                share()
            );
        }

        return this._currentUnsyncedChangesUpload$;
    }

    public addHorseGalleryPhoto(
        horseId: string,
        imageOptions: ImageOptions,
        mediaCollectionName: MediaCollectionName
    ): Observable<MediaData> {
        return this._addUnsyncedImageToCache(horseId, imageOptions, mediaCollectionName);
    }

    public addHorseGalleryVideo(mediaData: MediaData): Observable<MediaData> {
        return this._addUnsyncedVideoToCache(mediaData);
    }

    public addRidePhoto(
        rideId: string,
        imageOptions: ImageOptions,
        isCurrentRidePhoto: boolean
    ): Observable<MediaData> {
        return this._addUnsyncedImageToCache(
            rideId,
            imageOptions,
            MediaCollectionName.Ride,
            isCurrentRidePhoto
        );
    }

    public addRideVideo(mediaData: MediaData, isCurrentRideVideo: boolean): Observable<MediaData> {
        return this._addUnsyncedVideoToCache(mediaData, isCurrentRideVideo);
    }

    public getRideMedia(rideId: string): Observable<Media[]> {
        return forkJoin([
            this._cachedUnsyncedRideImagesMetadata(),
            this._cachedUnyncedRideVideosMetadata()
        ]).pipe(
            map(([photosMetadata, videosMetadata]) => [
                ...photosMetadata.filter(m => m.mediaSubjectId === rideId),
                ...videosMetadata.filter(m => m.mediaSubjectId === rideId)
            ]),
            switchMap(metadata => this._appendSafeUrlsToMetadata(metadata)),
            map(data =>
                data.map(
                    d =>
                        new Media({
                            _id: d.mediaId,
                            collectionId: d.mediaSubjectId,
                            thumbnail: new BaseMediaDocument({
                                safeUrl: d.safeUrl
                            })
                        })
                )
            )
        );
    }

    public migrateCurrentRideMediaToUploadQueue(): Observable<void> {
        return this._cachedCurrentRideImagesMetadata().pipe(
            switchMap(imagesMetaData => {
                return this._cachedCurrentRideVideosMetadata().pipe(
                    map(videoMetaData => {
                        const metaData = imagesMetaData.concat(videoMetaData);
                        if (metaData.length) {
                            return this._moveMediaMetaDataToUploadQueue(metaData);
                        }
                        return of(undefined);
                    })
                );
            }),
            switchMap(() =>
                // remove current ride media from cache
                from(
                    // remove images metadata
                    this._storageService.clearUserData(Keys.CurrentRideImagesMetadata)
                ).pipe(
                    switchMap(() =>
                        from(
                            // remove videos metadata
                            this._storageService.clearUserData(Keys.CurrentRideVideosMetadata)
                        )
                    )
                )
            ),
            catchError(err => {
                console.error('Error migrating current ride media to upload queue', err);
                return of(undefined);
            })
        );
    }

    /**
     * removeCurrentRideMediaByMediaId
     * removes the media from the current ride cache
     * @param media
     */
    public removeCurrentRideMediaByMediaId(media: Media): Observable<void> {
        // determine if the media is a photo or video
        // and remove it from the correct cache
        const observable =
            media.latest.type === MediaDocumentType.Image
                ? this._cachedCurrentRideImagesMetadata()
                : this._cachedCurrentRideVideosMetadata();
        const key =
            media.latest.type === MediaDocumentType.Image
                ? Keys.CurrentRideImagesMetadata
                : Keys.CurrentRideVideosMetadata;

        return observable.pipe(
            map(toFilter => toFilter.filter(u => u.mediaId !== media._id)),
            switchMap(filtered =>
                from(this._storageService.setUserData(key, JSON.stringify(filtered)))
            ),
            switchMap(() =>
                // remove local blob wherever it might be
                forkJoin([
                    // remove thumbnail image / video
                    this._removeUserImageBlob(media.fileName, true),
                    // remove full size image
                    this._removeUserImageBlob(media.fileName, false)
                ]).pipe(map(() => undefined))
            )
        );
    }

    public getPinnedHorseMedia(horseId: string): Observable<Media[]> {
        /*
        TODO: Include Videos
        */
        return this._getPinnedHorseImageThumbnails(horseId);
    }

    public getHorseMedia(horseId: string): Observable<Media[]> {
        /*
        TODO: Include Videos
        */
        return this._getHorseImageThumbnails(horseId);
    }

    public pinHorseMedia(
        horseId: string,
        mediaToPin: string,
        pinnedMediaToReplace?: string
    ): Observable<void> {
        // do not filter to avoid truncating data on save
        return this._allGalleryImagesMetadata(false).pipe(
            map((dat): [MediaMetadata[], MediaMetadata[]] => {
                const synced = (dat as [MediaMetadata[], MediaMetadata[]])[0];
                const unsynced = (dat as [MediaMetadata[], MediaMetadata[]])[1];
                const toPinSyncedIdx = synced.findIndex(s => s.mediaId === mediaToPin);
                const toPinUnsyncedIdx = unsynced.findIndex(u => u.mediaId === mediaToPin);
                if (toPinSyncedIdx >= 0) {
                    const syncedToPin = {
                        ...synced[toPinSyncedIdx],
                        galleryCategory: GalleryCategory.Pinned
                    };
                    synced.splice(toPinSyncedIdx, 1, syncedToPin);
                }
                if (toPinUnsyncedIdx >= 0) {
                    const unsyncedToPin = {
                        ...unsynced[toPinUnsyncedIdx],
                        galleryCategory: GalleryCategory.Pinned
                    };
                    unsynced.splice(toPinUnsyncedIdx, 1, unsyncedToPin);
                }
                if (pinnedMediaToReplace) {
                    const replaceSyncedIdx = synced.findIndex(
                        s => s.mediaId === pinnedMediaToReplace
                    );
                    const replaceUnsyncedIdx = unsynced.findIndex(
                        u => u.mediaId === pinnedMediaToReplace
                    );
                    if (replaceSyncedIdx >= 0) {
                        const syncedToReplace = {
                            ...synced[replaceSyncedIdx],
                            galleryCategory: GalleryCategory.General
                        };
                        synced.splice(replaceSyncedIdx, 1, syncedToReplace);
                    }
                    if (replaceUnsyncedIdx >= 0) {
                        const unsyncedToReplace = {
                            ...unsynced[replaceUnsyncedIdx],
                            galleryCategory: GalleryCategory.General
                        };
                        unsynced.splice(replaceUnsyncedIdx, 1, unsyncedToReplace);
                    }
                }
                return [synced, unsynced];
            }),
            switchMap((dat: [MediaMetadata[], MediaMetadata[]]) =>
                forkJoin([
                    from(
                        this._storageService.setUserData(
                            Keys.GalleryImageThumbnailsMetadata,
                            JSON.stringify(dat[0])
                        )
                    ),
                    from(
                        this._storageService.setUserData(
                            Keys.UnsyncedGalleryImagesMetadata,
                            JSON.stringify(dat[1])
                        )
                    )
                ])
            ),
            switchMap(() => this._unsyncedPinActions()),
            map(unsynced => [
                ...unsynced,
                {
                    horseId,
                    mediaToPin,
                    pinnedMediaToReplace: pinnedMediaToReplace ? pinnedMediaToReplace : undefined
                }
            ]),
            switchMap(unsyncedPinActions =>
                from(
                    this._storageService.setUserData(
                        Keys.UnsyncedPinActions,
                        JSON.stringify(unsyncedPinActions)
                    )
                )
            )
        );
    }

    public deleteHorseGalleryMedia(mediaId: string, horseId: string): Observable<void> {
        /*
        TODO: work with videos
        */
        return this._cachedGalleryImageThumbnailsMetadata().pipe(
            // check synced galllery images
            map(synced => {
                const idx = synced.findIndex(
                    s => s.mediaId === mediaId && s.mediaSubjectId === horseId
                );
                const save = idx >= 0;
                if (save) {
                    synced.splice(idx, 1);
                }
                return { synced, save };
            }),
            switchMap((v: { synced: MediaMetadata[]; save: boolean }) =>
                !v.save
                    ? // if found in synced, need to cache a delete action and upload it later
                      of(undefined)
                    : from(
                          this._storageService.setUserData(
                              Keys.GalleryImageThumbnailsMetadata,
                              JSON.stringify(v.synced)
                          )
                      ).pipe(
                          switchMap(() =>
                              this._cachedUnsyncedGalleryMediaToDelete().pipe(
                                  map(unsynced => [...unsynced, { mediaId, horseId }]),
                                  switchMap(allUnsynced =>
                                      from(
                                          this._storageService.setUserData(
                                              Keys.UnsyncedGalleryMediaToDelete,
                                              JSON.stringify(allUnsynced)
                                          )
                                      )
                                  )
                              )
                          )
                      )
            ),
            switchMap(() =>
                // check unsynced gallery images
                this._cachedUnsyncedGalleryImagesMetadata().pipe(
                    map(unsynced => {
                        const idx = unsynced.findIndex(
                            u => u.mediaId === mediaId && u.mediaSubjectId === horseId
                        );
                        const save = idx >= 0;
                        if (save) {
                            unsynced.splice(idx, 1);
                        }
                        return { unsynced, save };
                    }),
                    switchMap((v: { unsynced: MediaMetadata[]; save: boolean }) =>
                        !v.save
                            ? of(undefined)
                            : from(
                                  this._storageService.setUserData(
                                      Keys.UnsyncedGalleryImagesMetadata,
                                      JSON.stringify(v.unsynced)
                                  )
                              )
                    )
                )
            ),
            switchMap(() =>
                // remove local blob wherever it might be
                forkJoin([
                    this._removeUserImageBlob(mediaId, true),
                    this._removeUserImageBlob(mediaId, false)
                ]).pipe(map(() => undefined))
            )
        );
    }

    private _getHorseImageThumbnails(horseId: string): Observable<Media[]> {
        return this._allGalleryImagesMetadata(true, horseId).pipe(
            map(metadata => metadata as MediaMetadata[]),
            switchMap(metadata => this._appendSafeUrlsToMetadata(metadata)),
            map(data =>
                data.map(
                    d =>
                        new Media({
                            _id: d.mediaId,
                            collectionId: d.mediaSubjectId,
                            thumbnail: new BaseMediaDocument({
                                safeUrl: d.safeUrl
                            })
                        })
                )
            )
        );
    }

    private _getPinnedHorseImageThumbnails(horseId: string): Observable<Media[]> {
        return this._allGalleryImagesMetadata(true, horseId).pipe(
            map(metadata =>
                (metadata as MediaMetadata[]).filter(
                    m => m.galleryCategory === GalleryCategory.Pinned
                )
            ),
            switchMap(metadata => this._appendSafeUrlsToMetadata(metadata)),
            map(data =>
                data.map(
                    d =>
                        new Media({
                            _id: d.mediaId,
                            collectionId: d.mediaSubjectId,
                            thumbnail: new BaseMediaDocument({
                                safeUrl: d.safeUrl
                            })
                        })
                )
            )
        );
    }

    private _uploadUnsyncedGalleryImages(): Observable<void> {
        if (!this._currentUnsyncedGalleryImagesUpload$) {
            this._currentUnsyncedGalleryImagesUpload$ =
                this._cachedUnsyncedGalleryImagesMetadata().pipe(
                    map(meta => {
                        const observables: Observable<void>[] = [];
                        for (let _m = 0; _m < meta.length; ++_m) {
                            observables.push(this._uploadUnsyncedGalleryImage());
                        }
                        return serialSwitchMap(observables);
                    }),
                    switchMap(serial => serial),
                    tap(() => (this._currentUnsyncedGalleryImagesUpload$ = null)),
                    share()
                );
        }
        return this._currentUnsyncedGalleryImagesUpload$;
    }

    private _uploadUnsyncedGalleryImage(): Observable<void> {
        return this._cachedUnsyncedGalleryImagesMetadata().pipe(
            switchMap(unsynced =>
                unsynced.length === 0
                    ? of(undefined)
                    : of(unsynced).pipe(
                          map(allUnsynced => allUnsynced[0]),
                          switchMap((v: MediaMetadata) =>
                              this._getBlob(v.fileName, v.mediaType, false).pipe(
                                  map(blob => ({
                                      blob,
                                      imageId: v.mediaId,
                                      horseId: v.mediaSubjectId
                                  }))
                              )
                          ),
                          switchMap((v: { horseId: string; imageId: string; blob: Blob }) =>
                              this._galleryService
                                  .uploadImageToHorseGallery(v.horseId, v.imageId, v.blob)
                                  .pipe(
                                      map(() => ({
                                          mediaSubjectId: v.horseId,
                                          mediaId: v.imageId
                                      })),
                                      switchMap((meta: MediaMetadata) =>
                                          // do not filter to avoid truncating data on save
                                          this._galleryImagesRequiringServerThumbnail().pipe(
                                              map(requiring => [...requiring, meta])
                                          )
                                      ),
                                      switchMap(allRequiring =>
                                          from(
                                              this._storageService.setUserData(
                                                  Keys.GalleryImagesRequiringServerThumbnail,
                                                  JSON.stringify(allRequiring)
                                              )
                                          )
                                      ),
                                      switchMap(() =>
                                          this._migrateMetadata(
                                              0,
                                              Keys.UnsyncedGalleryImagesMetadata,
                                              Keys.GalleryImageThumbnailsMetadata
                                          )
                                      )
                                  )
                          )
                      )
            )
        );
    }

    private _migrateMetadata(idx: number, fromKey: Keys, toKey: Keys): Observable<void> {
        return from(this._storageService.getUserData<MediaMetadata[]>(fromKey, false)).pipe(
            map(json => {
                const fromData = this._parseMediaMetadata(json);
                const migrant = fromData[idx];
                fromData.splice(idx, 1);
                return { splicedJson: JSON.stringify(fromData), migrant };
            }),
            switchMap((v: { splicedJson: string; migrant: MediaMetadata }) =>
                from(this._storageService.setUserData(fromKey, v.splicedJson)).pipe(
                    map(() => v.migrant)
                )
            ),
            switchMap((migrant: MediaMetadata) =>
                from(this._storageService.getUserData<MediaMetadata[]>(toKey, false)).pipe(
                    map(json => {
                        const toData = this._parseMediaMetadata(json);
                        toData.push(migrant);
                        return JSON.stringify(toData);
                    })
                )
            ),
            switchMap((appendedJson: string) =>
                from(this._storageService.setUserData(toKey, appendedJson))
            )
        );
    }

    public preparePhotoData(subjectId: string, imageOptions: ImageOptions): Observable<MediaData> {
        return this._capacitorPluginService.getPhoto(imageOptions).pipe(
            switchMap(photo =>
                !photo
                    ? of(undefined)
                    : of(photo).pipe(
                          switchMap(p =>
                              this._thumbnailService
                                  .getPhotoThumbnail(
                                      p,
                                      THUMBNAIL_PIXEL_DIMENSIONS,
                                      THUMBNAIL_PIXEL_DIMENSIONS
                                  )
                                  .pipe(
                                      map(thumbnailDataUrl => ({
                                          dataUrl: p.dataUrl,
                                          thumbnailDataUrl,
                                          mediaSubjectId: subjectId,
                                          mediaId: createObjectID(),
                                          fileSize: base64Size(p.dataUrl),
                                          filePath: p.path,
                                          fileName: `${Date.now()}.${p.format}`,
                                          mediaType: MediaDocumentType.Image
                                      }))
                                  )
                          ),
                          switchMap((v: MediaData) =>
                              forkJoin([
                                  this._fileUrlToBlob(v.dataUrl).pipe(
                                      switchMap(blob => this._storeBlob(v, blob, false))
                                  ),
                                  this._fileUrlToBlob(v.thumbnailDataUrl).pipe(
                                      switchMap(blob => this._storeBlob(v, blob, true))
                                  )
                              ]).pipe(map(() => v))
                          )
                      )
            ),
            catchError(err => {
                console.error('[MediaCache] Error preparing photo data:', err);
                return of(undefined);
            })
        );
    }

    private _addUnsyncedImageToCache(
        subjectId: string,
        imageOptions: ImageOptions,
        mediaCollectionName: MediaCollectionName,
        isCurrentRidePhoto: boolean = false
    ): Observable<MediaData> {
        return this.preparePhotoData(subjectId, imageOptions).pipe(
            switchMap(photo =>
                !photo
                    ? of(undefined)
                    : of(photo).pipe(
                          switchMap((v: MediaData) => {
                              if (isCurrentRidePhoto) {
                                  // do not filter to avoid truncating data on save

                                  /**
                                   * for current ride image we need to cache the metadata
                                   */
                                  return this._cachedCurrentRideImagesMetadata().pipe(
                                      map(unsynced => [
                                          ...unsynced,
                                          {
                                              mediaId: v.mediaId,
                                              mediaSubjectId: v.mediaSubjectId,
                                              mediaType: MediaDocumentType.Image,
                                              mediaCollectionName,
                                              fileSize: v.fileSize,
                                              filePath: v.filePath,
                                              fileName: v.fileName
                                          }
                                      ]),
                                      switchMap(allUnsynced =>
                                          from(
                                              this._storageService.setUserData(
                                                  Keys.CurrentRideImagesMetadata,
                                                  JSON.stringify(allUnsynced)
                                              )
                                          )
                                      ),
                                      map(() => v)
                                  );
                              } else {
                                  /**
                                   * for any other images we don't need to cache the metadata
                                   * just add the images to the upload queue and return the media data
                                   * */
                                  return this._store
                                      .dispatch(
                                          new AddItemToQueueAction(
                                              new MediaUploadItem({
                                                  ...v,
                                                  mediaCollectionName,
                                                  fileSizeInString: bytesToSize(v.fileSize),
                                                  uploadedFileSizeInString: bytesToSize(0)
                                              })
                                          )
                                      )
                                      .pipe(map(() => v));
                              }
                          })
                      )
            ),
            catchError(err => {
                return throwError(() => err);
            })
        );
    }

    public prepareVideoData(mediaData: MediaData): Observable<MediaData> {
        return of(mediaData).pipe(
            take(1),
            switchMap((v: MediaData) =>
                this._fileUrlToBlob(v.thumbnailDataUrl)
                    .pipe(switchMap(blob => this._storeBlob(v, blob, true)))
                    .pipe(map(() => v))
            ),
            catchError(err => {
                console.error('[MediaCache] Error preparing video data:', err);
                return of(undefined);
            })
        );
    }

    public prepareDocumentData(mediaData: MediaData): Observable<MediaData> {
        return of(mediaData).pipe(
            take(1),
            switchMap((v: MediaData) =>
                this._fileUrlToBlob(v.dataUrl)
                    .pipe(switchMap(blob => this._storeBlob(v, blob, true)))
                    .pipe(map(() => v))
            ),
            catchError(err => {
                console.error('[MediaCache] Error preparing document data:', err);
                return of(undefined);
            })
        );
    }

    private _addUnsyncedVideoToCache(
        mediaData: MediaData,
        isCurrentRideVideo: boolean = false
    ): Observable<MediaData> {
        return this.prepareVideoData(mediaData).pipe(
            switchMap((v: MediaData) => {
                if (isCurrentRideVideo) {
                    // do not filter to avoid truncating data on save

                    /**
                     * for current ride video we need to cache the metadata
                     */
                    return this._cachedCurrentRideVideosMetadata().pipe(
                        take(1),
                        map(unsynced => [...unsynced, v]),
                        switchMap(allUnsynced => {
                            return from(
                                this._storageService.setUserData(
                                    Keys.CurrentRideVideosMetadata,
                                    JSON.stringify(allUnsynced)
                                )
                            );
                        }),
                        map(() => v)
                    );
                } else {
                    /**
                     * for any other video we don't need to cache the metadata
                     * just add the video to the upload queue and return the media data
                     * */
                    return this._store
                        .dispatch(
                            new AddItemToQueueAction(
                                new MediaUploadItem({
                                    ...v,
                                    fileSizeInString: bytesToSize(mediaData.fileSize),
                                    uploadedFileSizeInString: bytesToSize(0),
                                    thumbnailUrl: v.thumbnailDataUrl
                                })
                            )
                        )
                        .pipe(map(() => v));
                }
            })
        );
    }

    private _syncHorseGalleryImageThumbnailsCache(): Observable<void> {
        if (!this._currentGalleryImageThumbnailsDownload$) {
            this._currentGalleryImageThumbnailsDownload$ =
                this._downloadHorseGalleryImageThumbnails().pipe(
                    tap(() => (this._currentGalleryImageThumbnailsDownload$ = null)),
                    share()
                );
        }
        return this._currentGalleryImageThumbnailsDownload$;
    }

    private _downloadHorseGalleryImageThumbnails(): Observable<void> {
        return this._horseCache.getHorsesForList().pipe(
            switchMap(details =>
                combineLatest([
                    of(details),
                    this._galleryService.getHorseGalleryMedia(
                        details.map(d => d._id),
                        MediaDocumentType.Image
                    )
                ]).pipe(take(1))
            ),
            map(([details, galleryImageMedia]) =>
                serialSwitchMap(
                    details.map(d =>
                        this._downloadHorseGalleryImageThumbnailsByHorseId(d._id, galleryImageMedia)
                    )
                )
            ),
            switchMap(serial => serial)
        );
    }

    private _downloadHorseGalleryImageThumbnailsByHorseId(
        horseId: string,
        galleryImageMedia: Media[]
    ): Observable<void> {
        return forkJoin([
            this._cachedGalleryImageThumbnailsMetadata(horseId),
            this._galleryImagesRequiringServerThumbnail(horseId)
        ]).pipe(
            map(([cachedMetadata, metadataRequiringThumbnail]) =>
                galleryImageMedia.filter(m => {
                    const horseMatch = m.collectionId === horseId;
                    const absent = !cachedMetadata.find(meta => meta.mediaId === m._id);
                    const needsThumbnail = !!metadataRequiringThumbnail.find(
                        meta => meta.mediaId === m._id
                    );
                    return horseMatch && (absent || needsThumbnail);
                })
            ),
            map(media => {
                const observables: Observable<void>[] = [];
                for (const m of media) {
                    observables.push(
                        this._fileUrlToBlob(m.thumbnail.url).pipe(
                            switchMap(blob =>
                                this._storeBlob(
                                    {
                                        ...m,
                                        mediaId: m._id,
                                        mediaSubjectId: m.collectionId
                                    },
                                    blob,
                                    true
                                )
                            ),
                            switchMap(() =>
                                // do not filter to avoid truncating data on save
                                this._cachedGalleryImageThumbnailsMetadata().pipe(
                                    map(cached => {
                                        /*
                        Filter out duplicates that can appear from
                        mixing of gallery image metadata list and
                        list of metadata that requires a server-side
                        thumbnail
                    */
                                        const meta = {
                                            mediaId: m._id,
                                            mediaSubjectId: horseId,
                                            galleryCategory: m.galleryCategory
                                                ? m.galleryCategory
                                                : undefined
                                        };
                                        const idx = cached.findIndex(c => c.mediaId === m._id);
                                        if (idx >= 0) {
                                            cached.splice(idx, 1, meta);
                                            return cached;
                                        } else {
                                            return [...cached, meta];
                                        }
                                    }),
                                    switchMap(metadata =>
                                        from(
                                            this._storageService.setUserData(
                                                Keys.GalleryImageThumbnailsMetadata,
                                                JSON.stringify(metadata)
                                            )
                                        ).pipe(map(() => metadata[metadata.length - 1]))
                                    )
                                )
                            ),
                            switchMap(justDownloaded =>
                                // do not filter by horse id to avoid truncating other entries on save
                                this._galleryImagesRequiringServerThumbnail().pipe(
                                    map(needsThumbnail => {
                                        const idx = needsThumbnail.findIndex(
                                            r => r.mediaId === justDownloaded.mediaId
                                        );
                                        if (idx >= 0) {
                                            needsThumbnail.splice(idx, 1);
                                            return needsThumbnail;
                                        } else {
                                            return null;
                                        }
                                    }),
                                    switchMap(spliced =>
                                        !spliced
                                            ? of(undefined)
                                            : from(
                                                  this._storageService.setUserData(
                                                      Keys.GalleryImagesRequiringServerThumbnail,
                                                      spliced
                                                  )
                                              )
                                    )
                                )
                            )
                        )
                    );
                }
                return serialSwitchMap(observables);
            }),
            switchMap(serial => serial)
        );
    }

    private _syncPinActionsCache(): Observable<void> {
        if (!this._currentUnsyncedPinActionsUpload$) {
            this._currentUnsyncedPinActionsUpload$ = this._uploadUnsyncedPinActions().pipe(
                tap(() => (this._currentUnsyncedPinActionsUpload$ = null)),
                share()
            );
        }
        return this._currentUnsyncedPinActionsUpload$;
    }

    private _uploadUnsyncedPinActions(): Observable<void> {
        /*
        needs to done serially because order
        might matter in successive replacements
    */
        return this._unsyncedPinActions().pipe(
            switchMap(unsynced =>
                unsynced.length === 0
                    ? of(undefined)
                    : of(unsynced).pipe(
                          map(dtos =>
                              serialSwitchMap(dtos.map(dto => this._galleryService.pinMedia(dto)))
                          ),
                          switchMap(serial => serial)
                      )
            )
        );
    }

    private _syncGalleryDeletesCache(): Observable<void> {
        if (!this._currentUnsyncedGalleryDeletesUpload$) {
            this._currentUnsyncedGalleryDeletesUpload$ =
                this._uploadUnsyncedGalleryMediaDeletes().pipe(
                    tap(() => (this._currentUnsyncedGalleryDeletesUpload$ = null)),
                    share()
                );
        }
        return this._currentUnsyncedGalleryDeletesUpload$;
    }

    private _uploadUnsyncedGalleryMediaDeletes(): Observable<void> {
        return this._cachedUnsyncedGalleryMediaToDelete().pipe(
            switchMap(dtos =>
                dtos.length === 0
                    ? of(undefined)
                    : forkJoin([
                          ...dtos.map(dto => this._galleryService.deleteGalleryMedia(dto))
                      ]).pipe(map(() => undefined))
            )
        );
    }

    /*
      Transformations
    */

    private _fileUrlToBlob(url: string): Observable<Blob> {
        return from(fetch(url)).pipe(switchMap(resp => from(resp.blob()).pipe()));
    }

    private _parseMediaMetadata(json: MediaMetadata[]): MediaMetadata[] {
        return Array.isArray(json) ? json : [];
    }

    private _parsePinActions(json: PinMediaDto[]): PinMediaDto[] {
        return Array.isArray(json) ? json : [];
    }

    private _parseGalleryDeletes(json: DeleteGalleryMediaDto[]): DeleteGalleryMediaDto[] {
        return Array.isArray(json) ? json : [];
    }

    public getUserImageUrl(
        fileName: string,
        mediaType: MediaDocumentType,
        thumbnail: boolean = true
    ): Observable<{ url: string; safeUrl: SafeResourceUrl }> {
        return this._getBlob(fileName, mediaType, thumbnail).pipe(
            // filter(blob => !!blob),
            map(blob => {
                if (blob) {
                    return URL.createObjectURL(blob);
                }
                return undefined;
            }),
            map(url => ({
                url,
                safeUrl: this._sanitizer.bypassSecurityTrustResourceUrl(url)
            }))
        );
    }

    private _appendSafeUrlsToMetadata(meta: MediaMetadata[]): Observable<MediaData[]> {
        const observables: Observable<MediaData>[] = meta.map(m =>
            this.getUserImageUrl(m.fileName, m.mediaType).pipe(
                map(imageUrls => ({
                    ...m,
                    safeUrl: imageUrls.safeUrl
                }))
            )
        );
        return forkJoin(observables).pipe(
            defaultIfEmpty([]),
            map(results => {
                let all = [];
                for (const r of results) {
                    all = [...all, r];
                }
                return all;
            })
        );
    }

    /*
      Blob Storage
    */

    private _getBlob(
        fileName: string,
        mediaType: MediaDocumentType,
        thumbnail: boolean
    ): Observable<Blob> {
        return from(this._storageService.getBlob(fileName, mediaType, thumbnail));
    }

    private _storeBlob(metaData: MediaData, blob: Blob, thumbnail: boolean): Observable<void> {
        if (metaData.mediaType === MediaDocumentType.Document) {
            return from(
                this._storageService.storeBlob(
                    blob,
                    metaData.fileName,
                    metaData.mediaType,
                    thumbnail
                )
            );
        }

        return this._enforceMaxImageSize(blob).pipe(
            switchMap(newBlob => {
                return from(
                    this._storageService.storeBlob(
                        newBlob,
                        metaData.fileName,
                        metaData.mediaType,
                        thumbnail
                    )
                );
            })
        );
    }

    private _enforceMaxImageSize(blob: Blob, quality: number = 60): Observable<Blob> {
        return from(
            new Promise<Blob>((resolve, reject) => {
                const image = new Image();
                image.src = URL.createObjectURL(blob);

                image.onload = () => {
                    const aspectRatio = image.width / image.height;
                    const newWidth = image.width > 1020 ? 1020 : image.width; //enforce image width as 1060 or lower.
                    const newHeight = newWidth / aspectRatio;

                    const canvas: HTMLCanvasElement = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    canvas.width = newWidth;
                    canvas.height = newHeight;

                    ctx.drawImage(image, 0, 0, newWidth, newHeight);

                    // Convert the canvas back to a Blob with the desired format (e.g., JPEG)
                    canvas.toBlob(
                        newBlob => {
                            resolve(newBlob);
                        },
                        'image/jpeg',
                        quality
                    ); //quality is set to 60
                };

                image.onerror = error => {
                    reject(error);
                };
            })
        );
    }

    private _removeUserImageBlob(fileName: string, thumbnail): Observable<void> {
        return from(this._storageService.removeBlob(fileName, thumbnail));
    }

    /*
      Image Metadata Storage
    */

    private _cachedGalleryImageThumbnailsMetadata(horseId?: string): Observable<MediaMetadata[]> {
        return from(
            this._storageService.getUserData<MediaMetadata[]>(
                Keys.GalleryImageThumbnailsMetadata,
                false
            )
        ).pipe(
            map(json => this._parseMediaMetadata(json)),
            map(metadata =>
                !horseId ? metadata : metadata.filter(m => m.mediaSubjectId === horseId)
            )
        );
    }

    private _cachedUnsyncedGalleryImagesMetadata(horseId?: string): Observable<MediaMetadata[]> {
        return from(
            this._storageService.getUserData<MediaMetadata[]>(
                Keys.UnsyncedGalleryImagesMetadata,
                false
            )
        ).pipe(
            map(json => this._parseMediaMetadata(json)),
            map(metadata =>
                !horseId ? metadata : metadata.filter(m => m.mediaSubjectId === horseId)
            )
        );
    }

    private _allGalleryImagesMetadata(
        concatenate: boolean,
        horseId?: string
    ): Observable<MediaMetadata[] | [MediaMetadata[], MediaMetadata[]]> {
        return forkJoin([
            this._cachedGalleryImageThumbnailsMetadata(horseId),
            this._cachedUnsyncedGalleryImagesMetadata(horseId)
        ]).pipe(
            map(([synced, unsynced]) =>
                concatenate ? [...synced, ...unsynced] : [synced, unsynced]
            )
        );
    }

    private _galleryImagesRequiringServerThumbnail(horseId?: string): Observable<MediaMetadata[]> {
        return from(
            this._storageService.getUserData<MediaMetadata[]>(
                Keys.GalleryImagesRequiringServerThumbnail,
                false
            )
        ).pipe(
            map(json => this._parseMediaMetadata(json)),
            map(metadata =>
                !horseId ? metadata : metadata.filter(m => m.mediaSubjectId === horseId)
            )
        );
    }

    private _cachedUnsyncedGalleryMediaToDelete(): Observable<DeleteGalleryMediaDto[]> {
        return from(
            this._storageService.getUserData<DeleteGalleryMediaDto[]>(
                Keys.UnsyncedGalleryMediaToDelete,
                false
            )
        ).pipe(map(json => this._parseGalleryDeletes(json)));
    }

    private _unsyncedPinActions(): Observable<PinMediaDto[]> {
        return from(
            this._storageService.getUserData<PinMediaDto[]>(Keys.UnsyncedPinActions, false)
        ).pipe(map(json => this._parsePinActions(json)));
    }

    private _cachedCurrentRideImagesMetadata(): Observable<MediaMetadata[]> {
        return from(
            this._storageService.getUserData<MediaMetadata[]>(Keys.CurrentRideImagesMetadata, false)
        ).pipe(map(json => this._parseMediaMetadata(json)));
    }

    private _cachedCurrentRideVideosMetadata(): Observable<MediaMetadata[]> {
        return from(
            this._storageService.getUserData<MediaMetadata[]>(Keys.CurrentRideVideosMetadata, false)
        ).pipe(map(json => this._parseMediaMetadata(json)));
    }

    private _cachedUnsyncedRideImagesMetadata(): Observable<MediaMetadata[]> {
        return from(
            this._storageService.getUserData<MediaMetadata[]>(
                Keys.UnsyncedRideImagesMetadata,
                false
            )
        ).pipe(map(json => this._parseMediaMetadata(json)));
    }

    private _cachedUnsyncedRideVideosMetadata(): Observable<MediaMetadata[]> {
        return from(
            this._storageService.getUserData<MediaMetadata[]>(
                Keys.UnsyncedRideVideosMetadata,
                false
            )
        ).pipe(map(json => this._parseMediaMetadata(json)));
    }

    /*
      Video Metadata Storage
    */

    private _cachedUnsyncedGalleryVideosMetadata(): Observable<MediaMetadata[]> {
        return from(
            this._storageService.getUserData<MediaMetadata[]>(
                Keys.UnsyncedGalleryVideosMetadata,
                false
            )
        ).pipe(map(json => this._parseMediaMetadata(json)));
    }

    private _cachedUnyncedRideVideosMetadata(): Observable<MediaMetadata[]> {
        return from(
            this._storageService.getUserData<MediaMetadata[]>(
                Keys.UnsyncedRideVideosMetadata,
                false
            )
        ).pipe(map(json => this._parseMediaMetadata(json)));
    }

    private _moveMediaMetaDataToUploadQueue(metaData: MediaMetadata[]): Observable<void[]> {
        const observables: Observable<void>[] = [];
        for (const m of metaData) {
            observables.push(
                this._addMediaToUploadQueue(m, {
                    isMediaCreatedOnBackend: false
                })
            );
        }
        return forkJoin(observables);
    }

    private _addMediaToUploadQueue(
        metaData: MediaMetadata,
        extraParams?: Partial<MediaUploadItem>
    ): Observable<void> {
        return this._store.dispatch(
            new AddItemToQueueAction(
                new MediaUploadItem({
                    ...metaData,
                    mediaSubjectId: metaData.mediaSubjectId,
                    fileSizeInString: bytesToSize(metaData.fileSize),
                    uploadedFileSizeInString: bytesToSize(0),
                    ...extraParams
                })
            )
        );
    }
}
